import { useQuery } from '@tanstack/react-query';
import { Button, Table as AntTable } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { KTCard, KTCardBody } from '@/_metronic/helpers';
import { axiosClient } from '@/api/axiosClient';
import { TableGlobalSearch } from '@/components/molecules/TableGlobalSearch';
import { Table } from '@/components/organisms/Table';
import { useQueryRequest } from '@/hooks/useQueryRequest';
import { useTable } from '@/hooks/useTable';
import { getColumnDef } from '@/modules/operate-worker/components/table/_columns';
import { useGetWorkerList } from '@/modules/worker/hooks/useGetWorkerList';
import { WorkerModel } from '@/modules/worker/models';
import { history } from '@/utils';

interface Props {
  breadcrumbName: (value: string) => void;
}

interface OperatingFactoryRow {
  customer_id: string;
  customer_name: string;
  factory_id: string;
  factory_name: string;
  working_count: number;
  area?: string;
  customer_code?: string;
  managers?: string;
}

// QC #19 / T-VH-01 — màn chính Vận hành là danh sách KH/Nhà máy đang vận
// hành (mã KH, tên KH, số NLĐ đang làm, khu vực, QLNM; QLNM chỉ thấy KH
// được phân — scope BE). Chọn 1 dòng để drill xuống danh sách NLĐ của nhà
// máy đó; từ đó drill tiếp vào chi tiết NLĐ như cũ.
const OperateWorkerList = ({ breadcrumbName }: Props) => {
  const [selectedFactory, setSelectedFactory] = useState<OperatingFactoryRow | null>(null);

  const {
    queryState,
    updateQueryState,
    updateSortStateByTable,
    getTableSortState,
    globalSearch,
    queryString,
    handleUpdateGlobalSearch,
  } = useQueryRequest();

  const { data: factoriesRes, isLoading: loadingFactories } = useQuery(
    ['operating-factories'],
    () => axiosClient.get('/operate-worker/factories'),
    { refetchOnWindowFocus: false }
  );
  const factoryRows =
    (factoriesRes as unknown as { data?: { rows?: OperatingFactoryRow[] } })?.data?.rows ?? [];

  const workerQuery = selectedFactory
    ? `${queryString}${queryString ? '&' : ''}factory_id=${selectedFactory.factory_id}`
    : '';
  const { isLoading, data: workerList } = useGetWorkerList(workerQuery, {
    enabled: !!selectedFactory,
  } as never);

  const { ...tableProps } = useTable<WorkerModel>(getTableSortState);
  const columnDef = useMemo(() => getColumnDef(tableProps.columnResizeMode), []);

  useEffect(() => {
    updateSortStateByTable(tableProps.sorting);
  }, [tableProps.sorting]);

  function handleRowClick(worker: WorkerModel) {
    breadcrumbName(worker.full_name || '');
    history.push(`${worker.id}`);
  }

  // ---- Tầng 1: danh sách KH/Nhà máy đang vận hành ----
  if (!selectedFactory) {
    return (
      <KTCard>
        <KTCardBody className="py-4">
          <AntTable<OperatingFactoryRow>
            columns={[
              { dataIndex: 'customer_code', key: 'code', title: 'Mã KH' },
              { dataIndex: 'customer_name', key: 'cus', title: 'Tên khách hàng' },
              { dataIndex: 'factory_name', key: 'fac', title: 'Nhà máy' },
              {
                dataIndex: 'working_count',
                key: 'working',
                title: 'Số NLĐ đang làm',
              },
              { dataIndex: 'area', key: 'area', render: (v: string) => v || '—', title: 'Khu vực' },
              {
                dataIndex: 'managers',
                key: 'mgr',
                render: (v: string) => v || '—',
                title: 'Quản lý nhà máy',
              },
            ]}
            dataSource={factoryRows}
            loading={loadingFactories}
            locale={{
              emptyText: 'Chưa có nhà máy nào đang vận hành (hoặc bạn chưa được phân nhà máy)',
            }}
            onRow={(record) => ({
              onClick: () => setSelectedFactory(record),
              style: { cursor: 'pointer' },
            })}
            rowKey="factory_id"
            size="middle"
          />
        </KTCardBody>
      </KTCard>
    );
  }

  // ---- Tầng 2: danh sách NLĐ của nhà máy đã chọn ----
  return (
    <KTCard>
      <div className="card-header border-0 pt-6">
        <div className="card-toolbar d-flex align-items-center">
          <Button className="me-3" onClick={() => setSelectedFactory(null)}>
            ← Danh sách KH
          </Button>
          <h5 className="m-0 me-4">
            {selectedFactory.customer_name} — {selectedFactory.factory_name}
          </h5>
          <TableGlobalSearch
            onChange={handleUpdateGlobalSearch}
            placeholder="Tên NLĐ / Mã NLĐ / SĐT / CCCD"
            value={globalSearch}
          />
        </div>
      </div>
      <KTCardBody className="py-4">
        <Table
          {...tableProps}
          className="cursor-pointer"
          columns={columnDef}
          currentPage={queryState.page}
          data={workerList?.rows}
          isLoading={isLoading}
          limit={queryState.limit}
          onLimitChange={(limit) => updateQueryState({ limit })}
          onPageChange={(page) => updateQueryState({ page })}
          onRowClick={handleRowClick}
        />
      </KTCardBody>
    </KTCard>
  );
};

const OperateWorkerListListWrapper = (props: Props) => (
  <OperateWorkerList breadcrumbName={props.breadcrumbName} />
);

export { OperateWorkerListListWrapper };
